import React from 'react';
import { ClockIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { RunStatus } from '../../types';
import Tooltip from '../common/Tooltip';

interface EnhancedProgressBarProps {
  progress: number;
  status: RunStatus;
  currentStep: number;
  totalSteps: number;
  estimatedTimeRemaining?: number;
  isLoading?: boolean;
  showDetails?: boolean;
  className?: string;
}

const EnhancedProgressBar: React.FC<EnhancedProgressBarProps> = ({
  progress,
  status,
  currentStep,
  totalSteps,
  estimatedTimeRemaining,
  isLoading = false,
  showDetails = true,
  className = '',
}) => {
  const getProgressColor = (status: RunStatus) => {
    switch (status) {
      case 'running':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'waiting_for_input':
        return 'bg-orange-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getProgressBgColor = (status: RunStatus) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 dark:bg-blue-900/30';
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/30';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30';
      case 'paused':
        return 'bg-yellow-100 dark:bg-yellow-900/30';
      case 'waiting_for_input':
        return 'bg-orange-100 dark:bg-orange-900/30';
      default:
        return 'bg-gray-100 dark:bg-gray-700';
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      case 'running':
      case 'paused':
      case 'waiting_for_input':
        return <ClockIcon className="w-4 h-4 text-primary-600" />;
      default:
        return null;
    }
  };

  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  const progressColor = getProgressColor(status);
  const progressBgColor = getProgressBgColor(status);

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Progress bar */}
      <div className="relative">
        <div className={`w-full h-3 ${progressBgColor} rounded-full overflow-hidden`}>
          <div
            className={`h-full ${progressColor} transition-all duration-500 ease-out ${
              status === 'running' ? 'animate-pulse' : ''
            }`}
            style={{ width: `${clampedProgress}%` }}
          >
            {/* Animated stripe for running state */}
            {status === 'running' && (
              <div className="h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
            )}
          </div>
        </div>
        
        {/* Progress percentage overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {Math.round(clampedProgress)}%
          </span>
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <span className="text-gray-600 dark:text-gray-400">
              Step {currentStep} of {totalSteps}
            </span>
            {totalSteps > 0 && (
              <span className="text-gray-400">
                ({currentStep}/{totalSteps})
              </span>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {/* Estimated time remaining */}
            {estimatedTimeRemaining && estimatedTimeRemaining > 0 && status === 'running' && (
              <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                <ClockIcon className="w-3 h-3" />
                <span className="text-xs">
                  ~{formatTime(estimatedTimeRemaining)} remaining
                </span>
              </div>
            )}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step indicators for small step counts */}
      {totalSteps <= 10 && totalSteps > 0 && (
        <div className="flex items-center space-x-1">
          {Array.from({ length: totalSteps }, (_, index) => {
            const stepNumber = index + 1;
            const isCompleted = stepNumber <= currentStep;
            const isCurrent = stepNumber === currentStep + 1;
            
            return (
              <Tooltip content={`Step ${stepNumber}`}>
              <div
                key={stepNumber}
                className={`w-6 h-2 rounded-full transition-all duration-300 ${
                  isCompleted
                    ? progressColor
                    : isCurrent && status === 'running'
                    ? `${progressColor} animate-pulse`
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnhancedProgressBar;